import React, { useMemo } from 'react'
import { Stack, Typography, Tooltip, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ProcessCardProps } from '../../../types'
import { ProcessStatus } from './ProcessStatus'
import { 
  getProcessDates, 
  getProcessStatus, 
  getUniqueCategories, 
  formatDateRange, 
  formatInProgressDate 
} from '../../../helpers'
import { CATEGORIES_PROJECT } from '../../../constants'
import ProcessLink from './ProcessLink'

export const ProcessCard: React.FC<ProcessCardProps> = ({ process, isLast }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const dates = useMemo(() => getProcessDates(process), [process])
  const processStatus = useMemo(() => getProcessStatus(process), [process])
  const categories = useMemo(() => getUniqueCategories(process.services), [process.services])

  const displayDate = useMemo(() => {
    if (processStatus === 'completed') {
      return formatDateRange(dates, t)
    }
    
    if (processStatus === 'in_progress' && process.start_at && !process.end_at) {
      return formatInProgressDate(process, t)
    }
    
    return null
  }, [dates, processStatus, process, t])

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        padding: 2,
        borderBottom: !isLast ? `1px solid ${theme.palette.grey[200]}` : 'none',
        gap: 1,
        height: 140,
        width: '100%'
      }}
    >
      <Stack direction="row" gap={1}>
        {categories.map((category, index) => (
          <Tooltip 
            key={index}
            title={
              <ul style={{ 
                margin: 0, 
                padding: 0, 
                listStyle: 'none', 
                textAlign: 'center'
              }}>
                {process.services
                  .filter(service => service.category === category)
                  .map((service, idx) => (
                    <li key={idx} style={{ margin: '4px 0' }}>
                      {t(`projects.services.name.${service.name}`)}
                    </li>
                  ))
                }
              </ul>
            } 
            placement="top" 
            arrow
          >
            <i
              className={`fa-regular fa-${CATEGORIES_PROJECT[category]?.icon || 'circle-question'}`}
              style={{ 
                color: theme.palette.primary.main,
                fontSize: 14
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      {displayDate && (
        <Typography 
          component="span" 
          sx={{ 
            fontSize: 12,
            color: '#989898',
            textAlign: 'center'
          }}
        >
          {displayDate}
        </Typography>
      )}

      {processStatus && <ProcessStatus status={processStatus} />}
      <ProcessLink process={process} />
    </Stack>
  )
}
